import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatPts } from '../utils';

// Groups the flat /v1/clans/players aggregate into per-clan buckets.
function groupByClan(players) {
  const byClan = new Map();
  for (const p of players) {
    const name = p.Clan?.Name ?? 'Unknown';
    if (!byClan.has(name)) {
      byClan.set(name, {
        name,
        icon:        p.Clan?.Icon ?? null,
        countryCode: p.Clan?.CountryCode ?? null,
        place:       p.Clan?.Place ?? null,
        players:     [],
        totalActivePoints: 0,
        totalDiamonds:     0,
      });
    }
    const c = byClan.get(name);
    c.players.push(p);
    c.totalActivePoints += Number(p.ActiveBattlePoints || 0);
    c.totalDiamonds     += Number(p.AllTimeDiamonds || 0);
  }
  return [...byClan.values()];
}

export default function GlobalClans() {
  const [raw, setRaw]         = useState(null);
  const [tracked, setTracked] = useState(null);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('');
  const [onlyUntracked, setOnlyUntracked] = useState(false);
  const [sortMode, setSortMode] = useState('points');
  const [openClan, setOpenClan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.globalClanPlayers(), api.clans().catch(() => [])])
      .then(([g, c]) => {
        setRaw(g);
        setTracked(new Set(c.map(x => x.name.toLowerCase())));
      })
      .catch(e => setError(e.message));
  }, []);

  const clans = useMemo(() => {
    if (!raw) return [];
    const grouped = groupByClan(raw.players || []);
    for (const c of grouped) c.isTracked = tracked?.has(c.name.toLowerCase()) ?? false;
    return grouped;
  }, [raw, tracked]);

  const visible = useMemo(() => {
    const q = filter.toLowerCase();
    let rows = clans;
    if (q) rows = rows.filter(c => c.name.toLowerCase().includes(q));
    if (onlyUntracked) rows = rows.filter(c => !c.isTracked);
    const out = [...rows];
    switch (sortMode) {
      case 'name':
        out.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'diamonds':
        out.sort((a, b) => b.totalDiamonds - a.totalDiamonds);
        break;
      case 'members':
        out.sort((a, b) => b.players.length - a.players.length);
        break;
      case 'points':
      default:
        out.sort((a, b) => b.totalActivePoints - a.totalActivePoints);
    }
    return out;
  }, [clans, filter, onlyUntracked, sortMode]);

  if (error) return <div className="page"><div className="error-box">{error}</div></div>;
  if (!raw)  return <div className="page-center"><div className="spinner" /></div>;

  const untrackedCount = clans.filter(c => !c.isTracked).length;

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <div className="page-header-title">Global Clans</div>
          <div className="page-header-sub">
            Sampled top {raw.sampledClans} clans from BigGames' public API — includes clans our bot has never tracked.
          </div>
        </div>
      </div>

      <div className="stats-row mb-20">
        <div className="stat-card">
          <div className="stat-val">{clans.length}</div>
          <div className="stat-lbl">Clans in Sample</div>
        </div>
        <div className="stat-card">
          <div className="stat-val text-green">{clans.length - untrackedCount}</div>
          <div className="stat-lbl">Tracked by Bot</div>
        </div>
        <div className="stat-card">
          <div className="stat-val text-red">{untrackedCount}</div>
          <div className="stat-lbl">Not Tracked</div>
        </div>
        <div className="stat-card">
          <div className="stat-val">{raw.players?.length ?? 0}</div>
          <div className="stat-lbl">Players in Sample</div>
        </div>
      </div>

      <div className="filter-bar">
        <input
          className="filter-input"
          placeholder="Filter clans…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <select value={sortMode} onChange={e => setSortMode(e.target.value)}>
          <option value="points">Sort: Active Battle Points</option>
          <option value="diamonds">Sort: All-Time Diamonds</option>
          <option value="members">Sort: Member Count</option>
          <option value="name">Sort: Name (A→Z)</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', color: 'var(--muted)' }}>
          <input
            type="checkbox"
            checked={onlyUntracked}
            onChange={e => setOnlyUntracked(e.target.checked)}
          />
          only untracked
        </label>
      </div>

      {visible.length === 0 && <div className="empty-msg">No clans match your filter.</div>}

      {visible.length > 0 && (
        <div className="standings-wrap">
          {visible.map((c, i) => (
            <GlobalClanRow
              key={c.name}
              clan={c}
              rank={i + 1}
              open={openClan === c.name}
              onToggle={() => setOpenClan(openClan === c.name ? null : c.name)}
              onNavPlayer={id => navigate(`/player/${id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GlobalClanRow({ clan, rank, open, onToggle, onNavPlayer }) {
  const sortedPlayers = [...clan.players].sort((a, b) => b.ActiveBattlePoints - a.ActiveBattlePoints);
  const maxPts = sortedPlayers[0]?.ActiveBattlePoints || 1;
  const rankClass = rank <= 3 ? `rank-${rank}` : '';

  return (
    <div className={`clan-row ${rankClass}`}>
      <button className={`clan-row-btn${open ? ' expanded' : ''}`} onClick={onToggle}>
        <div><span className="rank-num">{rank}</span></div>

        <div>
          {clan.icon
            ? <img className="clan-icon" src={`https://biggamesapi.io/image/${clan.icon.split('://')[1]}`} alt="" />
            : <div className="clan-icon-ph">{clan.name[0]?.toUpperCase()}</div>}
        </div>

        <div className="clan-name-cell">
          <span className="clan-name-text">{clan.name}</span>
          {clan.countryCode && <span className="clan-level" style={{ marginLeft: 6 }}>{clan.countryCode}</span>}
          {!clan.isTracked && (
            <span className="badge badge-amber" style={{ marginLeft: 8 }}>Not Tracked</span>
          )}
        </div>

        <div className="clan-pts-cell">
          <span className="clan-pts-num">{formatPts(clan.totalActivePoints)} active</span>
        </div>

        <span className="clan-members">{clan.players.length} players</span>

        <span className="clan-chevron">▾</span>
      </button>

      <div className={`clan-panel${open ? ' open' : ''}`}>
        <div className="clan-panel-inner">
          <div className="player-list">
            {sortedPlayers.map(p => {
              const barPct = (Number(p.ActiveBattlePoints || 0) / maxPts * 100).toFixed(1);
              return (
                <div className="player-row" key={p.UserID}>
                  <div className="player-avatar-wrap">
                    <div className="player-avatar-ph">{(p.DisplayName?.[0] ?? '?').toUpperCase()}</div>
                  </div>
                  <div>
                    <button
                      className="player-name-btn"
                      onClick={e => { e.stopPropagation(); onNavPlayer(p.UserID); }}
                    >
                      {p.DisplayName}
                    </button>
                    <div className="player-pts-row">
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${barPct}%` }} />
                      </div>
                      <span className="player-pts">{formatPts(p.ActiveBattlePoints)}</span>
                    </div>
                    <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: 2 }}>
                      {formatPts(p.AllTimeDiamonds)} all-time diamonds
                      {p.TotalBattles ? ` · ${p.TotalBattles} battles` : ''}
                      {p.EarnedMedals ? ` · ${p.EarnedMedals} medals` : ''}
                    </div>
                  </div>
                  <a
                    className="player-roblox-link"
                    href={`https://www.roblox.com/users/${p.UserID}/profile`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                  >↗</a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
